import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Progress, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { CreatorPlatformResponse } from "@/api/creator";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";
import { formatNumber } from "@/shared/utils/formatters";
import {
	MergedAudienceAgeItem,
	formatPercentage,
	formatEngagementRate,
	normalizeDirectPercentage,
	hasMetricValue,
	mergeAudienceAgeData,
	NO_DATA_TEXT,
} from "@/shared/utils/socialProfile";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import { Icon, type IconName } from "@/shared/components/Icon";

// ─── Styled components ────────────────────────────────────────────────────────
import type { SocialPlatform as SocialPlatformType } from "@/shared/types/socialPlatform";

const Card = styled.div<{ $isActive: boolean; $isExpanded: boolean }>`
	background: white;
	border: 2px solid #e0e0e0;
	border-radius: 12px;
	padding: 20px;
	cursor: pointer;
	transition: all 0.2s;
	width: 100%;
	grid-column: ${(props) => (props.$isExpanded ? "span 2" : "span 1")};

	&:hover {
		border-color: #4460ef;
		box-shadow: 0 2px 8px rgba(68, 96, 239, 0.2);
	}

	@media (max-width: 768px) {
		grid-column: span 1;
	}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
`;

const PlatformIcon = styled(Icon)`
	width: 32px;
	height: 32px;
	flex-shrink: 0;
`;

const Title = styled.h3`
	font-size: 16px;
	font-weight: 600;
	color: #222;
	margin: 0;
`;

const InputContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const InputLabel = styled.label`
	font-size: 14px;
	font-weight: 500;
	color: #666;
`;

const Input = styled.input`
	width: 100%;
	padding: 10px 12px;
	border: 1px solid #d9d9d9;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s;

	&:focus {
		border-color: #4460ef;
	}

	&::placeholder {
		color: #999;
	}
`;

const CheckButton = styled.button<{ $enabled: boolean }>`
	padding: 10px 20px;
	background: ${(props) => (props.$enabled ? "#4460ef" : "#d9d9d9")};
	border: none;
	border-radius: 6px;
	color: white;
	font-size: 14px;
	font-weight: 600;
	cursor: ${(props) => (props.$enabled ? "pointer" : "not-allowed")};
	transition: all 0.2s;
	margin-top: 4px;
	align-self: flex-start;

	&:hover {
		opacity: ${(props) => (props.$enabled ? 0.9 : 1)};
	}
`;

const LoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	gap: 16px;
	margin-top: 20px;
`;

const LoadingText = styled.p`
	font-size: 16px;
	color: #666;
	margin: 0;
`;

const CreatorDataSection = styled.div`
	margin-top: 8px;
	padding-top: 24px;
	border-top: 2px solid #e0e0e0;
	background: #f9fbff;
	margin-left: -20px;
	margin-right: -20px;
	margin-bottom: -20px;
	padding-left: 20px;
	padding-right: 20px;
	padding-bottom: 20px;
	border-bottom-left-radius: 12px;
	border-bottom-right-radius: 12px;
`;

const CreatorHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	margin-bottom: 18px;
`;

const CreatorAvatarImage = styled(ResponsiveAvatar)`
	width: 56px;
	height: 56px;
	border-radius: 50%;
	object-fit: cover;
	background: linear-gradient(45deg, #4460ef, #8b9cff);
	flex-shrink: 0;
`;

const CreatorInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
`;

const CreatorName = styled.h3`
	font-size: 17px;
	font-weight: 700;
	color: #222;
	margin: 0;
`;

const CreatorHandle = styled.p`
	font-size: 11px;
	color: #666;
	margin: 0;
	font-weight: 500;
`;

const DataGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
`;

const CategoryHeader = styled.div`
	display: grid;
	grid-template-columns: 1fr auto 80px 120px;
	padding: 8px 0;
	gap: 8px;
	column-gap: 8px;
	align-items: center;
`;

const CategoryTitle = styled.h4`
	font-size: 14px;
	color: #686868;
	font-weight: 700;
	line-height: 1;
	text-transform: uppercase;
	letter-spacing: 1px;
	margin: 0;

	&:nth-child(1) {
		padding-left: 0;
	}

	&:nth-child(3) {
		text-align: right;
		padding-right: 20px;
		font-size: 11px;
		font-weight: 600;
		color: #666;
		letter-spacing: 0.5px;
	}

	&:nth-child(4) {
		text-align: center;
		font-size: 11px;
		font-weight: 600;
		color: #666;
		letter-spacing: 0.5px;
	}
`;

const DataItem = styled.div`
	display: grid;
	grid-template-columns: 1fr auto 80px 120px;
	align-items: center;
	padding: 8px 0;
	gap: 8px;
	column-gap: 8px;
`;

const AudienceAgeDataItem = styled(DataItem)`
	grid-template-columns: minmax(56px, 80px) minmax(220px, 1fr) 80px 120px;
`;

const AudienceAgeRangeLabel = styled.span`
	white-space: nowrap;
	font-size: 14px;
	color: #222;
	font-weight: 400;
	text-transform: none;
`;

const AudienceAgeShowToClientsTitle = styled(CategoryTitle)`
	white-space: nowrap;
	font-size: 11px;
	font-weight: 600;
	color: #666;
	letter-spacing: 0.5px;
`;

const AudienceAgeHeader = styled(CategoryHeader)`
	margin-top: -6px;
`;

const AudienceAgeHeaderControl = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 6px;
	width: 100%;
`;

const DataLabel = styled.span`
	font-size: 11px;
	color: #666;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	white-space: nowrap;
`;

const DataValue = styled.span`
	font-size: 14px;
	color: #222;
	font-weight: 400;
	white-space: nowrap;
`;

const CheckboxContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

const CheckIcon = styled.span`
	font-size: 20px;
	color: #000000;
	font-weight: bold;
`;

const ToggleContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

const ToggleSwitch = styled.label`
	position: relative;
	display: inline-block;
	width: 44px;
	height: 24px;
	cursor: pointer;
`;

const ToggleInput = styled.input`
	opacity: 0;
	width: 0;
	height: 0;

	&:checked + span {
		background-color: #4460ef;
	}

	&:checked + span:before {
		transform: translateX(20px);
	}
`;

const ToggleSlider = styled.span<{ $disabled?: boolean }>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${(props) => (props.$disabled ? "#1e1e2e !important" : "#ccc")};
	border-radius: 24px;
	transition: 0.3s;

	&:before {
		position: absolute;
		content: "";
		height: 18px;
		width: 18px;
		left: 3px;
		bottom: 3px;
		background-color: ${(props) => (props.$disabled ? "#cdd6f4" : "white")};
		border-radius: 50%;
		transition: 0.3s;
	}
`;

const AudienceProgress = styled(Progress)`
	.ant-progress-outer {
		margin-inline-end: 0;
	}
`;

const AudienceAgeProgress = styled(AudienceProgress)<{ $maleSplit: number; $hasGenderData?: boolean }>`
	.ant-progress-bg {
		background: ${(props) =>
			props.$hasGenderData
				? `linear-gradient(
          90deg,
          var(--color-light-blue, #4BC9F1) 0%,
          var(--color-light-blue, #4BC9F1) ${props.$maleSplit}%,
          var(--color-pink, #F72586) ${props.$maleSplit}%,
          var(--color-pink, #F72586) 100%
        )`
				: "var(--color-light-blue, #4BC9F1)"} !important;
	}
`;

const AudienceAgeProgressCell = styled.div`
	width: 100%;
`;

const TooltipContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const TooltipLine = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #ffffff;
	font-weight: 500;
`;

const TooltipDot = styled.span<{ $color: string }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${(props) => props.$color};
	flex-shrink: 0;
`;

const AudienceLayout = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 16px;
	margin-top: 16px;
	align-items: start;
	border-top: 1px solid #e6ecff;
	padding-top: 12px;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_AVATAR = "/default_avatar.png";

const VISIBILITY_FIELDS = {
	followers: true,
	engagement: true,
	avgViews: true,
	localAudience: true,
	female: true,
	male: true,
	audienceAgeDistribution: true,
};

type VisibilityField = keyof typeof VISIBILITY_FIELDS;

interface SocialProfileCardProps {
	platform: Platform;
	value: string;
	onChange: (value: string) => void;
	onClick: () => void;
	onCheckAvailability: (platform: Platform) => void;
	isLoading?: boolean;
	creatorData?: CreatorPlatformResponse;
	lastCheckedValue?: string;
	onVisibilityChange?: (platform: Platform, field: VisibilityField, isVisible: boolean) => void;
}

const platformConfig = {
	tiktok: {
		icon: "tiktok",
		title: "TikTok Profile",
		inputLabel: "Enter TikTok profile link or handle",
	},
	instagram: {
		icon: "instagram",
		title: "Instagram Profile",
		inputLabel: "Enter Instagram profile link or handle",
	},
	facebook: {
		icon: "facebook",
		title: "Facebook Profile",
		inputLabel: "Enter Facebook profile link or handle",
	},
	youtube: {
		icon: "youtube",
		title: "YouTube Profile",
		inputLabel: "Enter YouTube profile link or handle",
	},
} satisfies Record<SocialPlatformType, { icon: IconName; title: string; inputLabel: string }>;

type Platform = SocialPlatformType;

interface SocialProfileCardProps {
	platform: Platform;
	value: string;
	onChange: (value: string) => void;
	onClick: () => void;
	onCheckAvailability: (platform: Platform) => void;
	isLoading?: boolean;
	creatorData?: CreatorPlatformResponse;
	lastCheckedValue?: string;
	onVisibilityChange?: (platform: Platform, field: VisibilityField, isVisible: boolean) => void;
}

export const SocialProfileCard: React.FC<SocialProfileCardProps> = ({
	platform,
	value,
	onChange,
	onClick,
	onCheckAvailability,
	isLoading = false,
	creatorData,
	lastCheckedValue,
	onVisibilityChange,
}) => {
	const config = platformConfig[platform];
	const hasValue = value.trim() !== "";
	const isExpanded = isLoading || !!creatorData;
	const hasInputChanged = value !== lastCheckedValue;
	const isButtonEnabled = hasValue && !isLoading && (!creatorData || hasInputChanged);

	const [showToClients, setShowToClients] = useState<{ [K in VisibilityField]: boolean }>(VISIBILITY_FIELDS);

	const platformAccount = creatorData?.socialAccounts?.find((account) => account.platform === platform);

	const [avatarSrc, setAvatarSrc] = useState(platformAccount?.avatarUrl || DEFAULT_AVATAR);

	useEffect(() => {
		setAvatarSrc(platformAccount?.avatarUrl || DEFAULT_AVATAR);
	}, [platformAccount?.avatarUrl]);

	const handleToggle = (field: VisibilityField) => {
		setShowToClients((prev) => {
			const nextValue = !prev[field];
			onVisibilityChange?.(platform, field, nextValue);
			return { ...prev, [field]: nextValue };
		});
	};

	const mergedAudienceAgeData: MergedAudienceAgeItem[] = mergeAudienceAgeData(
		Array.isArray(platformAccount?.audienceAgeDistribution) ? platformAccount!.audienceAgeDistribution : [],
	);
	const hasAudienceAgeData = mergedAudienceAgeData.length > 0;

	// ── Render helpers ──────────────────────────────────────────────────────────

	const renderCheckmarkOrDash = (exists: boolean) => (
		<CheckboxContainer>{exists ? <CheckIcon>✓</CheckIcon> : <span>--</span>}</CheckboxContainer>
	);

	const renderVisibilityToggle = (field: VisibilityField, enabled: boolean) => (
		<ToggleContainer>
			<ToggleSwitch style={{ cursor: enabled ? "pointer" : "not-allowed", opacity: enabled ? 1 : 0.5 }}>
				<ToggleInput
					type="checkbox"
					checked={showToClients[field]}
					onChange={() => enabled && handleToggle(field)}
					onClick={(e) => e.stopPropagation()}
					disabled={!enabled}
				/>
				<ToggleSlider $disabled={!enabled} />
			</ToggleSwitch>
		</ToggleContainer>
	);

	// ── JSX ─────────────────────────────────────────────────────────────────────

	return (
		<Card $isActive={false} $isExpanded={isExpanded} onClick={onClick}>
			<Header>
				<PlatformIcon name={config.icon} alt={platform} preserveOriginalColor />
				<Title>{config.title}</Title>
			</Header>

			<InputContainer>
				<InputLabel>{config.inputLabel}</InputLabel>
				<Input
					type="text"
					placeholder=""
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onClick={(e) => e.stopPropagation()}
				/>
				{(!creatorData || hasInputChanged) && (
					<CheckButton
						$enabled={isButtonEnabled}
						onClick={(e) => {
							e.stopPropagation();
							if (isButtonEnabled) onCheckAvailability(platform);
						}}
						disabled={!isButtonEnabled}
					>
						{isLoading ? "Finding..." : "Add Creator"}
					</CheckButton>
				)}
			</InputContainer>

			{isLoading && (
				<LoadingContainer>
					<Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "#4460ef" }} spin />} />
					<LoadingText>Retrieving creator insights</LoadingText>
				</LoadingContainer>
			)}

			{creatorData && !isLoading && platformAccount && (
				<CreatorDataSection>
					<CreatorHeader>
						<CreatorAvatarImage
							displaySize={56}
							src={avatarSrc}
							alt={creatorData.creator.name || "Creator avatar"}
							onError={() => {
								if (avatarSrc !== DEFAULT_AVATAR) setAvatarSrc(DEFAULT_AVATAR);
							}}
						/>
						<CreatorInfoContainer>
							<CreatorName>{creatorData.creator.name || NO_DATA_TEXT}</CreatorName>
							<CreatorHandle>
								{platformAccount.platformUsername || platformAccount.url || NO_DATA_TEXT}
							</CreatorHandle>
						</CreatorInfoContainer>
					</CreatorHeader>

					<AudienceLayout>
						{/* ── Metrics grid ── */}
						<DataGrid>
							<CategoryHeader>
								<CategoryTitle>Category</CategoryTitle>
								<CategoryTitle />
								<CategoryTitle>Fetch/Label</CategoryTitle>
								<CategoryTitle>Show to Clients</CategoryTitle>
							</CategoryHeader>

							<DataItem>
								<DataLabel>Followers</DataLabel>
								<DataValue>
									{hasMetricValue(platformAccount.followers)
										? formatNumber(platformAccount.followers)
										: NO_DATA_TEXT}
								</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.followers))}
								{renderVisibilityToggle("followers", hasMetricValue(platformAccount.followers))}
							</DataItem>

							<DataItem>
								<DataLabel>Avg. Video Views</DataLabel>
								<DataValue>
									{hasMetricValue(platformAccount.avgViews)
										? formatNumber(platformAccount.avgViews)
										: NO_DATA_TEXT}
								</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.avgViews))}
								{renderVisibilityToggle("avgViews", hasMetricValue(platformAccount.avgViews))}
							</DataItem>

							<DataItem>
								<DataLabel>Avg. Engagement Rate</DataLabel>
								{/* formatEngagementRate handles the ×100 conversion internally */}
								<DataValue>{formatEngagementRate(platformAccount.avgEngagementRate)}</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.avgEngagementRate))}
								{renderVisibilityToggle(
									"engagement",
									hasMetricValue(platformAccount.avgEngagementRate),
								)}
							</DataItem>

							<DataItem>
								<DataLabel>Local Audience %</DataLabel>
								<DataValue>{formatPercentage(platformAccount.localAudiencePercentage)}</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.localAudiencePercentage))}
								{renderVisibilityToggle(
									"localAudience",
									hasMetricValue(platformAccount.localAudiencePercentage),
								)}
							</DataItem>

							<DataItem>
								<DataLabel>Female %</DataLabel>
								<DataValue>{formatPercentage(platformAccount.femalePercentage)}</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.femalePercentage))}
								{renderVisibilityToggle("female", hasMetricValue(platformAccount.femalePercentage))}
							</DataItem>

							<DataItem>
								<DataLabel>Male %</DataLabel>
								<DataValue>{formatPercentage(platformAccount.malePercentage)}</DataValue>
								{renderCheckmarkOrDash(hasMetricValue(platformAccount.malePercentage))}
								{renderVisibilityToggle("male", hasMetricValue(platformAccount.malePercentage))}
							</DataItem>
						</DataGrid>

						{/* ── Age distribution grid ── */}
						<DataGrid>
							<AudienceAgeHeader>
								<CategoryTitle>Audience Age Distribution</CategoryTitle>
								<CategoryTitle />
								<CategoryTitle />
								<AudienceAgeHeaderControl>
									<AudienceAgeShowToClientsTitle>Show to Clients</AudienceAgeShowToClientsTitle>
									{renderVisibilityToggle("audienceAgeDistribution", hasAudienceAgeData)}
								</AudienceAgeHeaderControl>
							</AudienceAgeHeader>

							{hasAudienceAgeData ? (
								mergedAudienceAgeData.map((item, index) => {
									const hasGenderData = item.malePercentage + item.femalePercentage > 0;
									const maleSplit = hasGenderData
										? (item.malePercentage / (item.malePercentage + item.femalePercentage)) * 100
										: 50;

									return (
										<AudienceAgeDataItem key={`${item.ageRange}-${index}`}>
											<AudienceAgeRangeLabel>{item.ageRange}</AudienceAgeRangeLabel>
											<AudienceAgeProgressCell>
												<AppTooltip
													arrow={false}
													blurPx={0}
													title={
														<TooltipContent>
															{hasGenderData ? (
																<>
																	<TooltipLine>
																		<TooltipDot $color="var(--color-light-blue, #4BC9F1)" />
																		Male: {formatPercentage(item.malePercentage)}
																	</TooltipLine>
																	<TooltipLine>
																		<TooltipDot $color="var(--color-pink, #F72586)" />
																		Female:{" "}
																		{formatPercentage(item.femalePercentage)}
																	</TooltipLine>
																</>
															) : (
																<TooltipLine>Gender split unavailable</TooltipLine>
															)}
														</TooltipContent>
													}
												>
													<AudienceAgeProgress
														$maleSplit={maleSplit}
														$hasGenderData={hasGenderData}
														percent={normalizeDirectPercentage(item.percentage)}
														showInfo={false}
														trailColor="var(--color-pink-light, #FFE5F2)"
														strokeLinecap="round"
														strokeWidth={10}
													/>
												</AppTooltip>
											</AudienceAgeProgressCell>
											<span />
											<DataValue style={{ textAlign: "right" }}>
												{formatPercentage(item.percentage)}
											</DataValue>
										</AudienceAgeDataItem>
									);
								})
							) : (
								<DataItem>
									<DataLabel>Distribution</DataLabel>
									<DataValue>{NO_DATA_TEXT}</DataValue>
									<span />
								</DataItem>
							)}
						</DataGrid>
					</AudienceLayout>
				</CreatorDataSection>
			)}
		</Card>
	);
};
